/**
 * Entrar pela Steam, do lado do jogo.
 *
 * Quem abre o jogo pela Steam não digita nada: a compra e a identidade já
 * vieram de lá. O caminho tem três pedaços, e este arquivo é o do meio.
 *
 * 1. A casca do Tauri pede um ticket de sessão ao cliente da Steam
 *    (`src-tauri/src/steam.rs`).
 * 2. Aqui, o ticket vira uma chamada a `POST /v1/auth/steam`.
 * 3. O servidor confere o ticket e a posse na Web API da Valve, cria a conta se
 *    ainda não houver e devolve a sessão (`docs/STEAM.md`).
 *
 * **Nada disso é carregado no navegador.** A detecção é por farejamento de
 * `window`, e o `import()` do pacote do Tauri é dinâmico, como em
 * `game/saves/escolha.ts`: assim o pacote da web nunca baixa nem executa código
 * que só existe no computador.
 *
 * **Falhar aqui nunca é erro de tela.** Sem Steam rodando, sem SDK na build ou
 * com a Valve fora do ar, o jogo cai no caminho normal de entrar na conta. É
 * por isso que tudo aqui devolve `null` em vez de lançar: a ausência da Steam é
 * o caso comum fora dela, não uma falha.
 */

import { detectarPlataforma } from "../game/saves/escolha";

/** O que a casca devolve quando a Steam está por perto. */
export interface TicketDaSteam {
  ticket: string;
  steamId: string;
}

/**
 * A Steam só pode existir dentro do aplicativo de computador.
 *
 * No navegador não há SDK, e no Android a loja é outra. Perguntar antes evita
 * carregar o pacote do Tauri em lugar nenhum onde ele não serve.
 */
export function steamPossivel(): boolean {
  return detectarPlataforma() === "tauri";
}

export async function pedirTicketDaSteam(): Promise<TicketDaSteam | null> {
  if (!steamPossivel()) return null;

  try {
    const { invoke } = await import("@tauri-apps/api/core");
    const resposta = await invoke<{ ticket: string; steam_id: string }>("ticket_da_steam");
    if (!resposta?.ticket) return null;
    return { ticket: resposta.ticket, steamId: resposta.steam_id };
  } catch {
    // Build sem `--features steam`, Steam fechada, ou o SDK recusando: nenhum
    // dos três impede de jogar, e todos terminam na tela de entrar.
    return null;
  }
}
